import RavTable, { TableCellDescription } from '@/components/rav-table/rav-table';
import { Job, Job$Reason, Job$Status } from '@/types/Job';
import { Vue } from 'vue-class-component';
import JobsRepository, { JobsRepositoryQueryParams } from '@/repositories/jobs-repository';
import InitiatorField from '@/pages/transcoding/initiator-field';
import { FieldDescription, FieldDescription$Type } from '@/types/FieldDescription';
import FormatterFork from '@/components/formatter-fork/formatter-fork';
import { StandardOption } from '@/types/Component';
import { Job$Status as Job$StatusString } from '@Ravnur-Inc/shared/types/Job';
import TranscodingProgress from '@/pages/transcoding/transcoding-progress';
import DurationField from '@/pages/transcoding/duration-field';
import { EntityListActionId } from '@/types/EntityListAction';

import './transcoding-table.scss';

const CN = 'transcoding-table';

const DEFAULT_PARAMS: Partial<JobsRepositoryQueryParams> = {
  count: 4,
  sortType: 'desc',
  sortField: 'SubmittedOn',
  offset: 0,
  state: [
    Job$StatusString.QUEUED,
    Job$StatusString.ERROR,
    Job$StatusString.RUNNING,
    Job$StatusString.CANCELED,
    Job$StatusString.CANCELING,
  ],
};

type DashboardTranscodingActionOption = {
  label: string;
  process: (event: Job) => void;
};

const repository = new JobsRepository();

export default class TranscodingTable extends Vue {
  loading = false;
  list: Job[] = [];
  timeout: Nullable<NodeJS.Timeout> = null;

  get stateFieldThesaurus(): Array<StandardOption> {
    return [
      {
        id: Job$Status.QUEUED.toString(),
        label: this.$t('encoding', 'field__status-queued'),
        badge: 'warning',
      },
      {
        id: Job$Status.RUNNING.toString(),
        label: this.$t('encoding', 'field__status-processing'),
        badge: 'primary',
      },
      {
        id: Job$Status.ERROR.toString(),
        label: this.$t('encoding', 'field__status-failed'),
        badge: 'danger',
      },
      {
        id: Job$Status.CANCELED.toString(),
        label: this.$t('encoding', 'field__status-cancelled'),
        badge: 'secondary',
      },
      {
        id: Job$Status.CANCELLING.toString(),
        label: this.$t('encoding', 'field__status-cancelling'),
        badge: 'secondary',
      },
    ];
  }

  get reasonThesaurus(): Array<StandardOption<Job$Reason>> {
    return [
      {
        id: Job$Reason.UNKNOWN,
        label: this.$t('encoding', 'field__unknown'),
      },
      {
        id: Job$Reason.CC_GENERATION,
        label: this.$t('encoding', 'field__cc-generation'),
      },
      {
        id: Job$Reason.TRIMMING,
        label: this.$t('encoding', 'field__trimming'),
      },
      {
        id: Job$Reason.CLIPPING,
        label: this.$t('encoding', 'field__clipping'),
      },
      {
        id: Job$Reason.LIVE_STREAM,
        label: this.$t('encoding', 'field__live-stream'),
      },
      {
        id: Job$Reason.CHANGE_VERSION,
        label: this.$t('encoding', 'field__change-version'),
      },
      {
        id: Job$Reason.NEW_VERSION_UPLOAD,
        label: this.$t('encoding', 'field__new-version-upload'),
      },
      {
        id: Job$Reason.RETRANSCODING,
        label: this.$t('encoding', 'field__retranscoding'),
      },
      {
        id: Job$Reason.INITIAL_UPLOAD,
        label: this.$t('encoding', 'field__transcoding'),
      },
    ];
  }

  get slots() {
    return {
      initiator: this.renderInitiator,
      submittedOn: this.renderSubmittedOn,
      state: this.renderState,
      progress: this.renderProgress,
      reason: this.renderType,
      startedOn: this.renderDuration,
      actions: this.renderActions,
    };
  }

  render() {
    const descriptions: Array<TableCellDescription<Job>> = [
      { key: 'mediaId', label: this.$t('dashboard-transcoding-table', 'column__media-id') },
      { key: 'mediaTitle', label: this.$t('dashboard-transcoding-table', 'column__title') },
      { key: 'initiator', label: this.$t('dashboard-transcoding-table', 'column__initiator') },
      { key: 'submittedOn', label: this.$t('dashboard-transcoding-table', 'column__submitted-on') },
      { key: 'state', label: this.$t('dashboard-transcoding-table', 'column__status') },
      { key: 'progress', label: this.$t('dashboard-transcoding-table', 'column__progress') },
      { key: 'reason', label: this.$t('dashboard-transcoding-table', 'column__type') },
      { key: 'startedOn', label: this.$t('dashboard-transcoding-table', 'column__duration') },
      { key: 'actions', label: this.$t('dashboard-transcoding-table', 'column__actions') },
    ];

    return (
      <card class={CN} title="Current transcoding jobs">
        {this.loading && <spinner />}
        {this.list.length > 0 ? (
          <>
            <div class={`${CN}__table-wrapper`}>
              <RavTable
                class={`${CN}__table`}
                descriptions={descriptions}
                list={this.list}
                mode="striped"
                v-slots={this.slots}
              />
            </div>
            <div class={`${CN}__table-footer`}>
              <r-button color="primary" mode="frameless" onClick={this.toTranscodingPanel}>
                {this.$t('dashboard-transcoding-table', 'action__to-panel')}
              </r-button>
            </div>
          </>
        ) : (
          <div class={`${CN}__empty`} v-show={!this.loading}>
            <l10n group="dashboard-transcoding-table" tkey="info__no-events" />
          </div>
        )}
      </card>
    );
  }

  renderActions({ item }: { item: Job }) {
    const isDeletableJob = ({ state }: Job) =>
      state === Job$Status.ERROR ||
      state === Job$Status.CANCELLING ||
      state === Job$Status.CANCELED;

    const isRetryableJob = ({ state }: Job) =>
      state === Job$Status.ERROR ||
      state === Job$Status.CANCELLING ||
      state === Job$Status.CANCELED;

    const isCancelableJob = ({ state }: Job) =>
      state === Job$Status.RUNNING || state === Job$Status.QUEUED;

    const actions = [];

    if (isCancelableJob(item)) {
      actions.push({
        id: EntityListActionId.Disable,
        label: this.$t('encoding', 'action__cancel'),
        predicate: isCancelableJob,
        process: async (item: Job) => {
          this.loading = true;
          await repository.cancel(item.id);
          this.fetchJobs();
        },
      });
    }

    if (isDeletableJob(item)) {
      actions.push({
        id: EntityListActionId.Delete,
        label: this.$t('encoding', 'action__delete'),
        predicate: isDeletableJob,
        process: async (item: Job) => {
          this.loading = true;
          await repository.remove(item.id);
          this.fetchJobs();
        },
      });
    }

    if (isRetryableJob(item)) {
      actions.push({
        id: EntityListActionId.Enable,
        label: this.$t('encoding', 'action__retry'),
        predicate: isRetryableJob,
        process: async (item: Job) => {
          this.loading = true;
          await repository.retry(item.id);
          this.fetchJobs();
        },
      });
    }

    return (
      <more-actions
        openerPosition="top"
        options={actions}
        onClick={(option: DashboardTranscodingActionOption) => option.process(item)}
      />
    );
  }

  renderDuration({ item }: { item: Job }) {
    return <DurationField entity={item} />;
  }

  renderType({ item }: { item: Job }) {
    const description = {
      type: FieldDescription$Type.THESAURUS,
      field: 'reason',
      thesaurus: this.reasonThesaurus,
    };

    return (
      <FormatterFork
        description={description as FieldDescription<Entity>}
        entity={item}
        value={item.reason}
      />
    );
  }

  renderProgress({ item }: { item: Job }) {
    return <TranscodingProgress entity={item} />;
  }

  renderState({ item }: { item: Job }) {
    const description = {
      type: FieldDescription$Type.THESAURUS,
      field: 'state',
      thesaurus: this.stateFieldThesaurus,
    };

    return (
      <FormatterFork
        description={description as FieldDescription<Entity>}
        entity={item}
        value={item.state}
      />
    );
  }

  renderSubmittedOn({ item }: { item: Job }) {
    const description = {
      type: FieldDescription$Type.DATE,
    };

    return (
      <FormatterFork
        description={description as FieldDescription<Entity>}
        entity={item}
        value={item.submittedOn}
      />
    );
  }

  renderInitiator({ item }: { item: Job }) {
    return <InitiatorField entity={item} />;
  }

  toTranscodingPanel() {
    this.$router.push({ name: 'Transcoding' });
  }

  async fetchJobs(params: Nullable<Partial<JobsRepositoryQueryParams>> = null, silence = false) {
    if (!silence) {
      this.loading = true;
    }

    let queryParams = DEFAULT_PARAMS;

    if (params) {
      queryParams = {
        ...queryParams,
        ...params,
      };
    }

    try {
      const { items } = await repository.load(queryParams);

      this.list = items;
    } catch (e) {
      this.$processException(e);
    } finally {
      this.loading = false;
    }
  }

  async startPooling() {
    await this.fetchJobs();

    this.timeout = setInterval(() => this.fetchJobs(null, true), 5000);
  }

  mounted() {
    this.startPooling();
  }

  beforeUnmount() {
    if (this.timeout) {
      clearInterval(this.timeout);
    }
  }
}
